import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from './api';
import styled from 'styled-components';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const Container = styled.div`
  max-width: 1200px;
  margin: 30px auto;
  padding: 20px;
  font-family: 'Inter', sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  background: white;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 20px;
    text-align: center;
  }
`;

const InfoBox = styled.div`
  h1 { margin: 0; font-size: 1.5rem; color: #1a1a1a; }
  p { margin: 5px 0 0; color: #666; }
`;

const ViewToggle = styled.div`
  display: flex;
  background: #f1f3f5;
  padding: 4px;
  border-radius: 8px;
`;

const CalendarWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  padding-bottom: 10px;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 10px;
  margin-top: 20px;
  min-width: 800px; // Evita que se amontone en móvil
`;

const DayHeader = styled.div`
  text-align: center;
  font-weight: 700;
  color: #adb5bd;
  text-transform: uppercase;
  font-size: 0.8rem;
  padding: 10px;
`;

const DayCell = styled.div`
  background: white;
  min-height: 120px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 5px;
  position: relative;
  
  &.has-schedule { border-left: 4px solid #1976d2; }
  &.not-month { opacity: 0.4; background: #fafafa; }
  
  .num { font-weight: 700; color: #333; margin-bottom: 5px; }
`;

const EventTag = styled.div`
  font-size: 0.75rem;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.type === 'E' ? '#e8f5e9' : props.type === 'S' ? '#fff3e0' : '#f3e5f5'};
  color: ${props => props.type === 'E' ? '#2e7d32' : props.type === 'S' ? '#ef6c00' : '#7b1fa2'};
  display: flex;
  justify-content: space-between;
`;

const ManualTag = styled.div`
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: auto;
  background: ${props => props.type === 'F' ? '#ffebee' : props.type === 'A' ? '#fff3e0' : '#e3f2fd'};
  color: ${props => props.type === 'F' ? '#c62828' : props.type === 'A' ? '#ef6c00' : '#1565c0'};
  font-weight: 600;
  text-align: center;
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.05);
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  min-width: 700px;
  
  th { background: #f8f9fa; padding: 15px; text-align: left; color: #495057; }
  td { padding: 15px; border-top: 1px solid #eee; vertical-align: top; }
`;

function ReportePersonal() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mes = searchParams.get('mes') || dayjs().format('YYYY-MM');
  
  const [data, setData] = useState(null);
  const [view, setView] = useState('calendar'); // 'calendar' or 'table'

  useEffect(() => {
    fetchData();
  }, [id, mes]);

  const fetchData = async () => {
    try {
      const res = await api.get(`/registros/personal?id_docente=${id}&mes=${mes}`);
      setData(res.data);
    } catch (error) {
      console.error(error);
    }
  };

  if (!data) return <p>Cargando reporte...</p>;

  const startDay = dayjs(`${mes}-01`).day();
  const blanks = Array(startDay).fill(null);

  return (
    <Container style={{ marginTop: '10px' }}>
      <button 
        onClick={() => navigate(-1)} 
        style={{ background: '#f1f3f5', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', marginBottom: '15px', fontWeight: 'bold', color: '#495057' }}
      >
        ← Volver
      </button>
      <Header style={{ padding: '15px', marginBottom: '15px' }}>
        <InfoBox>
          <h1>{data.docente}</h1>
          <p>CI: {data.ci} | Período: {dayjs(`${mes}-01`).format('MMMM YYYY')}</p>
        </InfoBox>
        <ViewToggle>
          <button 
            onClick={() => setView('calendar')} 
            style={{
              padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600',
              background: view === 'calendar' ? 'white' : 'transparent',
              boxShadow: view === 'calendar' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
              color: view === 'calendar' ? '#1976d2' : '#495057'
            }}
          >
            Calendario
          </button>
          <button 
            onClick={() => setView('table')} 
            style={{
              padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600',
              background: view === 'table' ? 'white' : 'transparent',
              boxShadow: view === 'table' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
              color: view === 'table' ? '#1976d2' : '#495057'
            }}
          >
            Tabla
          </button>
        </ViewToggle>
      </Header>

      {view === 'calendar' ? (
        <CalendarWrapper>
          <CalendarGrid>
            {['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'].map(d => <DayHeader key={d}>{d}</DayHeader>)}
            {blanks.map((_, i) => <DayCell key={`b-${i}`} className="not-month" />)}
            {data.report.map(day => (
              <DayCell key={day.fecha} className={day.tieneHorario ? 'has-schedule' : ''}>
                <div className="num">{dayjs(day.fecha).date()}</div>
                {day.eventos.map((ev, i) => (
                  <EventTag key={i} type={ev.tipo}>
                    <span>{ev.tipo === 'E' ? 'E' : 'S'}: {ev.hora}</span>
                  </EventTag>
                ))}
                {day.manual && (
                  <ManualTag type={day.manual.tipo}>
                    {day.manual.tipo}
                  </ManualTag>
                )}
              </DayCell>
            ))}
          </CalendarGrid>
        </CalendarWrapper>
      ) : (
        <TableWrapper>
          <StyledTable>
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Día</th>
                <th>Horario Esperado</th>
                <th>Registros Biométricos</th>
                <th>Reporte Manual</th>
              </tr>
            </thead>
            <tbody>
              {data.report.map(day => (
                <tr key={day.fecha} style={{background: day.tieneHorario ? 'transparent' : '#fdfdfd'}}>
                  <td>{dayjs(day.fecha).format('DD/MM/YYYY')}</td>
                  <td style={{textTransform: 'capitalize'}}>{dayjs(day.fecha).locale('es').format('dddd')}</td>
                  <td>
                    {day.horarios.map((h, i) => <div key={i}>{h.entrada} - {h.salida}</div>)}
                    {!day.tieneHorario && <span style={{color: '#ccc'}}>Sin horario</span>}
                  </td>
                  <td>
                    {day.eventos.map((ev, i) => (
                      <div key={i}>{ev.tipo === 'E' ? '🟢 E:' : '🔴 S:'} {ev.hora}</div>
                    ))}
                  </td>
                  <td>
                    {day.manual && (
                      <ManualTag type={day.manual.tipo} style={{display: 'inline-block', padding: '2px 8px'}}>
                        {day.manual.tipo} - {day.manual.obs || 'Sin obs.'}
                      </ManualTag>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>
        </TableWrapper>
      )}
    </Container>
  );
}

export default ReportePersonal;
